import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Loader2, MoreHorizontal } from "lucide-react";
import ActionBar from "./MItemActionBar/ActionBar";
import DynamicTable from "../../../components/Table/MTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { useSelector } from "react-redux";
import FileImport from "../FileImport";

const MItems = () => {
  const [items, setItems] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const { role } = useSelector((state) => state.auth);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [filteredItems, setFilteredItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  const itemsPerPage = isMobile ? 5 : 10;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/mitem/mitems`;

  useEffect(() => {
    if (items.length > 0) {
      const filtered = items.filter((item) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          (item.ItemName && item.ItemName.toLowerCase().includes(searchLower)) ||
          (item.CategoryID && item.CategoryID.toString().includes(searchLower)) ||
          (item.CategoryName && item.CategoryName.toLowerCase().includes(searchLower)) ||
          (item.Description && item.Description.toLowerCase().includes(searchLower)) ||
          (item.UnitName && item.UnitName.toLowerCase().includes(searchLower))
        );
      });
      setFilteredItems(filtered);
      setTotalPages(Math.ceil(filtered.length / itemsPerPage));
      setCurrentPage(1);
    }
  }, [searchQuery, items, itemsPerPage]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(apiEndpoint);
      if (!response.ok) throw new Error('Failed to fetch items');
      const data = await response.json();
      setItems(data);
      setFilteredItems(data);
      setTotalPages(Math.ceil(data.length / itemsPerPage));
    } catch (err) {
      setError("Failed to fetch items. Please try again.");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, itemsPerPage]);

  useEffect(() => {
    fetchItems();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fetchItems]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPagination = () => {
    const maxVisiblePages = 5;
    const pageNumbers = [];
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        pageNumbers.push(currentPage - 1);
        pageNumbers.push(currentPage);
        pageNumbers.push(currentPage + 1);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-4 mb-4">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="flex gap-1">
          {pageNumbers.map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-3 py-1">
                <MoreHorizontal className="h-5 w-5" />
              </span>
            ) : (
              <button
                key={`page-${page}`}
                onClick={() => paginate(page)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === page 
                    ? 'bg-blue-500 text-white' 
                    : 'hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            )
          ))}
        </div>

        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    );
  };

  const renderMobileView = () => (
    <div className="space-y-4 p-4">
      {loading ? (
        <div className="text-center py-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="mt-2">Loading items...</p>
        </div>
      ) : error ? (
        <div className="text-red-500 p-4 text-center border border-red-200 rounded-lg">
          {error}
        </div>
      ) : (
        currentItems.map((item) => (
          <div 
            key={item.ItemID} 
            className={`p-4 border rounded-lg shadow-sm ${
              selectedRow?.ItemID === item.ItemID ? 'bg-blue-50 border-blue-300' : 'bg-white'
            }`}
            onClick={() => setSelectedRow(item)}
          >
            <div className="flex justify-between">
              <h3 className="text-lg font-semibold">{item.ItemName}</h3>
              <span className="text-gray-500">ID: {item.ItemID}</span>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              <p>Category: {item.CategoryName}</p>
              <p>Description: {item.Description}</p>
              <p>Unit: {item.UnitName}</p>
            </div>
          </div>
        ))
      )}

      <div className="flex justify-between items-center mt-4">
        <button 
          onClick={() => paginate(currentPage - 1)} 
          disabled={currentPage === 1}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 flex items-center"
        >
          <ChevronLeft className="h-5 w-5 mr-1" /> Previous
        </button>
        <span className="text-sm">
          Page {currentPage} of {totalPages}
        </span>
        <button 
          onClick={() => paginate(currentPage + 1)} 
          disabled={currentPage === totalPages}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 flex items-center"
        >
          Next <ChevronRight className="h-5 w-5 ml-1" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header exportEndpoint={apiEndpoint} />
      <div className="flex-grow">
        <div className="flex flex-col sm:flex-row justify-between items-center p-4 gap-4">
          <ActionBar
            apiEndpoint={apiEndpoint}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            refreshData={fetchItems}
            rowKey="ItemID"
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            userRole="admin"
            data={items} // Pass the entire dataset
          />

          <div className="flex flex-col items-end gap-2">
            <FileImport 
              apiEndpoint={apiEndpoint}
              onImportSuccess={fetchItems}
            />
          </div>
        </div>

        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Item Table</h3>
        </div>

        {isMobile ? (
          renderMobileView()
        ) : (
          <div className="px-4">
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                <p className="mt-2">Loading items...</p>
              </div>
            ) : error ? (
              <div className="text-red-500 p-4 text-center border border-red-200 rounded-lg">
                {error}
              </div>
            ) : (
              <DynamicTable
                data={currentItems}
                columns={[
                  { key: "ItemID", label: "Item ID" },
                  { key: "CategoryName", label: "Category Name" },
                  { key: "ItemName", label: "Item Name" },
                  { key: "Description", label: "Description" },
                  { key: "UnitName", label: "Unit Name" },
                ]}
                rowKey="ItemID"
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={filteredItems.length}
                itemsPerPage={itemsPerPage}
                highlightColor="bg-blue-100"
                noDataMessage="No items found."
              />
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MItems;